import {
  AugmentedObjectTypeDefinition,
  AugmentedSchemaAST,
} from './semantic-analyzer/types'

/**
 * Generates the TypeScript code for a permissions SDK based on a zed schema.
 * @param schema The augmented schema AST.
 * @returns The generated TypeScript code as a string.
 */
export function generateSDK(
  schema: AugmentedSchemaAST,
  parserImport = '@schoolai/spicedb-zed-schema-parser/builder',
): string {
  const objectDefs = schema.definitions.filter(
    (def): def is AugmentedObjectTypeDefinition => def.type === 'definition',
  )

  let code = `// Generated by @schoolai/spicedb-zed-schema-parser
// Do not edit manually.

import { PermissionOperations, WriteOperation, Transaction, DeleteOperation, CheckOperation, BulkCheckOperation } from '${parserImport}';

// --------------- GENERIC TYPES ---------------

export type Subject<T extends string> = \`\${T}:\${string}\`;
export type Resource<T extends string> = \`\${T}:\${string}\`;

// --------------- RESOURCE TYPES ---------------
`

  for (const def of objectDefs) {
    code += `export type ${toPascalCase(def.name)}Resource = Resource<'${def.name}'>;\n`
  }

  code += `\nexport const permissions = {
  batch: (...operations: WriteOperation[]) => {
    const tx = new Transaction();
    for (const op of operations) tx.add(op);
    return tx;
  },
`

  for (const def of objectDefs) {
    if (def.relations.length === 0 && def.permissions.length === 0) {
      continue
    }
    code += `  ${toCamelCase(def.name)}: {\n`
    code += generateGrantRevoke(def)
    code += generateCheck(def)
    code += generateCheckBulk(def)
    code += generateFind(def)
    code += generateLookup(def)
    code += generateDeleteAll(def)
    code += `  },\n`
  }

  code += '};\n'

  code += generateDynamicDispatch(objectDefs)

  return code
}

function generateGrantRevoke(def: AugmentedObjectTypeDefinition): string {
  const resourceType = `${toPascalCase(def.name)}Resource`
  let code = '    grant: {\n'
  for (const rel of def.relations) {
    const subjectTypeLiterals = [
      ...new Set(rel.types.map(t => `'${t.typeName}'`)),
    ].join(' | ')
    const subjectTypes = `Subject<${subjectTypeLiterals || 'never'}>`
    code += `      ${toCamelCase(rel.name)}: (subject: ${subjectTypes} | ${subjectTypes}[], resource: ${resourceType}) => PermissionOperations.grant('${rel.name}').subject(subject).resource(resource),\n`
  }
  code += '    },\n'
  code += '    revoke: {\n'
  for (const rel of def.relations) {
    const subjectTypeLiterals = [
      ...new Set(rel.types.map(t => `'${t.typeName}'`)),
    ].join(' | ')
    const subjectTypes = `Subject<${subjectTypeLiterals || 'never'}>`
    code += `      ${toCamelCase(rel.name)}: (subject: ${subjectTypes} | ${subjectTypes}[], resource: ${resourceType}) => PermissionOperations.revoke('${rel.name}').subject(subject).resource(resource),\n`
  }
  code += '    },\n'
  return code
}

function generateFind(def: AugmentedObjectTypeDefinition): string {
  let code = '    find: {\n'
  for (const rel of def.relations) {
    const pascalRel = toPascalCase(rel.name)
    const subjectTypeLiterals = [
      ...new Set(rel.types.map(t => `'${t.typeName}'`)),
    ].join(' | ')
    const subjectTypes = `Subject<${subjectTypeLiterals || 'never'}>`
    code += `      by${pascalRel}: (subject: ${subjectTypes}) => PermissionOperations.find().relation('${rel.name}').subject(subject),\n`
  }
  code += '    },\n'
  return code
}

function generateCheck(def: AugmentedObjectTypeDefinition): string {
  const resourceType = `${toPascalCase(def.name)}Resource`
  let code = '    check: {\n'

  // Generate permission checks
  for (const perm of def.permissions) {
    const subjectTypeLiterals = [
      ...new Set(perm.inferredSubjectTypes?.map(t => `'${t.typeName}'`)),
    ].join(' | ')
    const subjectTypes = `Subject<${subjectTypeLiterals || 'never'}>`
    code += `      ${toCamelCase(perm.name)}: (subject: ${subjectTypes}, resource: ${resourceType}) => PermissionOperations.check('${perm.name}').subject(subject).resource(resource),\n`
  }

  // Generate relation checks
  for (const rel of def.relations) {
    const subjectTypeLiterals = [
      ...new Set(rel.types.map(t => `'${t.typeName}'`)),
    ].join(' | ')
    const subjectTypes = `Subject<${subjectTypeLiterals || 'never'}>`

    let checkName = `is${toPascalCase(rel.name)}`
    if (rel.docComment) {
      const checkMatch = rel.docComment.match(/@check:\s*(\w+)/)
      if (checkMatch?.[1]) {
        checkName = checkMatch[1]
      }
    }

    code += `      ${checkName}: (subject: ${subjectTypes}, resource: ${resourceType}) => PermissionOperations.check('${rel.name}').subject(subject).resource(resource),\n`
  }

  code += '    },\n'
  return code
}

function generateCheckBulk(def: AugmentedObjectTypeDefinition): string {
  const resourceType = `${toPascalCase(def.name)}Resource`
  const permissionLiterals = [
    ...new Set(def.permissions.map(p => `'${p.name}'`)),
  ].join(' | ')

  if (!permissionLiterals) return ''

  const subjectTypeLiterals = [
    ...new Set(
      def.relations.flatMap(r => r.types.map(t => `'${t.typeName}'`)),
    ),
  ].join(' | ')
  const subjectTypes = `Subject<${subjectTypeLiterals || 'never'}>`

  return `    checkBulk: (permission: ${permissionLiterals}, subject: ${subjectTypes}, resources: ${resourceType}[]) => PermissionOperations.bulkCheck(permission, subject, resources),\n`
}

function generateDeleteAll(def: AugmentedObjectTypeDefinition): string {
  const relationLiterals = [
    ...new Set(def.relations.map(r => `'${r.name}'`)),
  ].join(' | ')

  const subjectTypeLiterals = [
    ...new Set(
      def.relations.flatMap(r => r.types.map(t => `'${t.typeName}'`)),
    ),
  ].join(' | ')

  if (!relationLiterals) return ''

  let code = '    deleteAll: (filter: {\n'
  code += `      relation?: ${relationLiterals};\n`
  code += `      subjectType?: ${subjectTypeLiterals || 'never'};\n`
  code += '      subjectId?: string;\n'
  code += '      resourceId?: string;\n'
  code += `    }) => PermissionOperations.delete().where({ resourceType: '${def.name}', ...filter }),\n`

  return code
}

function generateLookup(def: AugmentedObjectTypeDefinition): string {
  const resourceType = `${toPascalCase(def.name)}Resource`
  const permissionLiterals = [
    ...new Set(def.permissions.map(p => `'${p.name}'`)),
  ].join(' | ')

  const subjectTypeLiterals = [
    ...new Set(
      def.relations.flatMap(r => r.types.map(t => `'${t.typeName}'`)),
    ),
  ].join(' | ')

  if (!permissionLiterals) return ''

  let code = '    lookup: {\n'
  code += `      resources: (subject: Subject<${subjectTypeLiterals || 'never'}>, permission: ${permissionLiterals}) => PermissionOperations.lookup().resourcesAccessibleBy(subject).withPermission(permission).ofType('${def.name}'),\n`
  code += `      subjects: (resource: ${resourceType}, permission: ${permissionLiterals}, subjectType: ${subjectTypeLiterals || 'never'}) => PermissionOperations.lookup().subjectsWithAccessTo(resource).withPermission(permission).ofType(subjectType),\n`
  code += '    },\n'
  return code
}

function generateDynamicDispatch(
  objectDefs: AugmentedObjectTypeDefinition[],
): string {
  const defsWithRelations = objectDefs.filter(d => d.relations.length > 0)
  const defsWithPermissions = objectDefs.filter(d => d.permissions.length > 0)

  if (defsWithRelations.length === 0) return ''

  let code = '\n// --------------- DYNAMIC DISPATCH ---------------\n\n'

  // ResourceType union
  const resourceTypes = defsWithRelations.map(d => `'${d.name}'`).join(' | ')
  code += `export type ResourceType = ${resourceTypes};\n\n`

  // GrantParams discriminated union
  code += 'export type GrantParams =\n'
  for (let i = 0; i < defsWithRelations.length; i++) {
    const def = defsWithRelations[i]!
    const relationLiterals = [
      ...new Set(def.relations.map(r => `'${r.name}'`)),
    ].join(' | ')
    const subjectTypeLiterals = [
      ...new Set(
        def.relations.flatMap(r => r.types.map(t => `'${t.typeName}'`)),
      ),
    ].join(' | ')
    const subjectTypes = `Subject<${subjectTypeLiterals || 'never'}>`
    const resourceType = `${toPascalCase(def.name)}Resource`
    const separator = i < defsWithRelations.length - 1 ? '' : ';\n'
    code += `  | { objectType: '${def.name}'; relation: ${relationLiterals}; subject: ${subjectTypes} | ${subjectTypes}[]; resource: ${resourceType} }${separator}\n`
  }

  // RevokeParams (same shape as GrantParams)
  code += '\nexport type RevokeParams = GrantParams;\n'

  // CheckParams discriminated union
  if (defsWithPermissions.length > 0) {
    code += '\nexport type CheckParams =\n'
    for (let i = 0; i < defsWithPermissions.length; i++) {
      const def = defsWithPermissions[i]!
      const permissionLiterals = [
        ...new Set(def.permissions.map(p => `'${p.name}'`)),
      ].join(' | ')
      const subjectTypeLiterals = [
        ...new Set(
          def.relations.flatMap(r => r.types.map(t => `'${t.typeName}'`)),
        ),
      ].join(' | ')
      const subjectTypes = `Subject<${subjectTypeLiterals || 'never'}>`
      const resourceType = `${toPascalCase(def.name)}Resource`
      const separator = i < defsWithPermissions.length - 1 ? '' : ';\n'
      code += `  | { objectType: '${def.name}'; permission: ${permissionLiterals}; subject: ${subjectTypes}; resource: ${resourceType} }${separator}\n`
    }
  }

  // Dispatcher functions
  code += `
export function dynamicGrant(params: GrantParams): WriteOperation {
  return PermissionOperations.grant(params.relation).subject(params.subject).resource(params.resource);
}

export function dynamicRevoke(params: RevokeParams): WriteOperation {
  return PermissionOperations.revoke(params.relation).subject(params.subject).resource(params.resource);
}

export function dynamicCheck(params: CheckParams): CheckOperation {
  return PermissionOperations.check(params.permission).subject(params.subject).resource(params.resource);
}
`

  return code
}

function toPascalCase(name: string): string {
  return name
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
}

function toCamelCase(name: string): string {
  if (name.length === 0) return name
  return (
    toPascalCase(name).charAt(0).toLowerCase() + toPascalCase(name).slice(1)
  )
}
